// THE RENEWAL LEAD AND THE FOLD (pure; no React). Two jobs, both read-only over the account payload.
//
// leadRenewal(data): the pending renewal the account page leads with. It is the SAME array the Terms section renders
// (payload.pending_terms, built once by services/v3/pending-terms.js); the term it renews is looked up by the
// renews.term_id that reader stated. Nothing here decides which session renews which term.
//
// emptyFacts(t): the term rows (settled, in motion, coverage) that would print only "not recorded" / "not stated",
// by the same tests the rows use. The fold in TermBlock.jsx hides exactly these rows and counts exactly this list; a
// row is hidden, never removed, and expands in place.
//
// periodFallback(t, key): a date the term's Dates row would show as "not recorded" that the period record knows.

export const FOLD_GROUPS = Object.freeze(['Settled', 'In motion', 'Coverage']);
// Every row the fold can hide, by group: emptyFacts only ever names these (the guard checks it).
export const FACT_LABELS = Object.freeze({
  Settled: Object.freeze(['Premium', 'Taxes', 'Fees', 'Commission', 'Billing']),
  'In motion': Object.freeze(['A/R', 'A/P', 'Carrier confirmed', 'Ledger']),
  Coverage: Object.freeze(['Liability', 'Medical', 'Hull', 'Deductibles']),
});

export function leadRenewal(data) {
  const pending = (Array.isArray(data?.pending_terms) ? data.pending_terms : []).find((p) => p && p.kind === 'renewal') || null;
  if (!pending) return null;
  const terms = Array.isArray(data.terms) ? data.terms : [];
  const term = pending.renews ? terms.find((t) => t.id === pending.renews.term_id) || null : null;
  const pilots = Array.isArray(pending.pilots) ? pending.pilots : [];
  const outstanding = pilots.filter((x) => x.state !== 'attested').length;
  const aircraft = (Array.isArray(data.aircraft) ? data.aircraft : []).filter((a) => !a.retired_at && !(a.link && a.link.ended_at));
  return { pending, term, pilots, outstanding, aircraft };
}

const blank = (v) => v == null || v === '';

export function emptyFacts(t) {
  const bind = t?.ledger?.bind || null;
  const c = t?.coverage || {};
  const liab = c.liability || {}; const hull = c.hull || {}; const ded = c.deductibles || {};
  const out = [];
  const add = (group, label, empty) => { if (empty) out.push({ group, label }); };
  const feesText = bind?.fees ? Object.keys(bind.fees).length === 0 : true;
  add('Settled', 'Premium', blank(bind?.premium));
  add('Settled', 'Taxes', blank(bind?.estimated_taxes));
  add('Settled', 'Fees', feesText);
  add('Settled', 'Commission', bind?.commission == null);
  add('Settled', 'Billing', !bind?.billing_method);
  add('In motion', 'A/R', !bind || (blank(bind.ar_payment_status) && !bind.ar_paid_on && !bind.ar_method && !bind.ar_financed_by));
  add('In motion', 'A/P', !bind || (blank(bind.ap_remit_status) && !bind.ap_remit_at));
  add('In motion', 'Carrier confirmed', !bind?.carrier_confirmed_at);
  add('In motion', 'Ledger', !t?.ledger);
  add('Coverage', 'Liability', blank(liab.per_occurrence) && blank(liab.per_passenger) && liab.smooth !== true);
  add('Coverage', 'Medical', blank(c.medical_payments));
  add('Coverage', 'Hull', blank(hull.value) && !hull.basis);
  add('Coverage', 'Deductibles', blank(ded.not_in_motion) && blank(ded.in_motion));
  return out;
}

/** { ymd, source } when the Dates row has no value for `key` (effective_date | expiration_date) and the period record does. */
export function periodFallback(t, key) {
  if (!blank(t?.dates?.[key])) return null;
  const v = t?.period?.[key];
  return blank(v) ? null : { ymd: v, source: t.period.source || null };
}
